/**
 * Everything said in answer to one message.
 *
 * Not a second kind of conversation with its own rules - a thread here is
 * simply the messages that point at the same parent, which the app already
 * knew and was throwing away at the point of drawing. Two games being argued
 * about at once stop interleaving, and nobody has to decide up front that a
 * thread is starting.
 *
 * It opens beside the chat rather than replacing it, because the reason you
 * are reading a thread is usually something that was said outside it.
 */

import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, socket } from '@/lib/api';
import type { Message } from '@/lib/types';
import { ago } from '@/lib/notifications';
import { cn } from '@/lib/cn';
import { Icon } from '@/icons/Icon';
import { Avatar } from './Avatar';
import { EmojiText } from './EmojiText';
import { UserName } from './user';
import { Button, Modal, useToast } from './ui';

export function ThreadPanel({ messageId, onClose }: { messageId: string; onClose: () => void }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);

  const q = useQuery({
    queryKey: ['thread', messageId],
    queryFn: () => api.get<{ parent: Message; replies: Message[] }>('/api/messages/thread/' + messageId)
  });

  /* A reply sent by anybody in the room lands here without a refresh. */
  useEffect(() => socket.on('message:new', (msg: Message) => {
    if (msg.replyTo?.id !== messageId) return;
    void queryClient.invalidateQueries({ queryKey: ['thread', messageId] });
  }), [messageId, queryClient]);

  const parent = q.data?.parent;
  const [following, setFollowing] = useState<boolean | null>(null);
  const isFollowing = following ?? !!parent?.following;

  async function follow() {
    if (!parent) return;
    const next = !isFollowing;
    setFollowing(next);
    try {
      await api.post('/api/messages/thread/' + parent.id + '/follow', { following: next });
      void queryClient.invalidateQueries({ queryKey: ['messages'] });
    } catch (err) {
      setFollowing(!next);
      toast((err as Error).message, 'bad');
    }
  }

  async function send() {
    if (!parent || !body.trim() || busy) return;
    setBusy(true);
    try {
      const payload: Record<string, unknown> = { body: body.trim(), replyTo: parent.id, scope: parent.scope };
      if (parent.scope === 'party') payload.channelId = parent.channelId;
      else if (parent.scope === 'crew') payload.crewId = parent.channelId.replace(/^cc:/, '');
      else if (parent.scope === 'group') payload.groupId = parent.channelId.replace(/^gc:/, '');
      else payload.username = parent.user?.username;

      await api.post('/api/messages', payload);
      setBody('');
      void queryClient.invalidateQueries({ queryKey: ['thread', messageId] });
      void queryClient.invalidateQueries({ queryKey: ['messages'] });
    } catch (err) {
      toast((err as Error).message, 'bad');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      side
      title="Thread"
      subtitle={parent
        ? 'Replies to ' + (parent.user?.displayName ?? 'a message') + '. They stay here rather than filling the room.'
        : 'Loading…'}
      onClose={onClose}
      footer={
        <>
          <input
            value={body}
            onChange={(e) => setBody(e.target.value.slice(0, 1000))}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); void send(); } }}
            placeholder="Reply in the thread…"
            data-enter-own
            className="h-10 min-w-0 flex-1 rounded-[calc(12px*var(--radius-mul,1))] bg-surface-2 px-3 text-[14px] outline-none shadow-[inset_0_0_0_1px_var(--line-soft)] placeholder:text-ghost focus:shadow-[inset_0_0_0_1px_var(--accent)]"
          />
          <Button kind="primary" disabled={busy || !body.trim()} onClick={() => void send()}>Send</Button>
        </>
      }
    >
      {q.isPending ? (
        <div className="flex flex-col gap-2">{[0, 1, 2].map((i) => <div key={i} className="h-16 animate-pulse rounded-[14px] bg-surface-2" />)}</div>
      ) : q.isError ? (
        <p className="m-0 py-8 text-center text-[13.5px] text-mute">{(q.error as Error).message}</p>
      ) : (
        <div className="flex flex-col gap-3">
          {/* Saying something here follows it; this is for wanting to know
              how it ends without saying anything. */}
          {parent ? (
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => void follow()}
                aria-pressed={isFollowing}
                className={cn(
                  'flex h-8 items-center gap-1.5 rounded-full px-3 text-[12.5px] font-semibold outline-none transition-colors',
                  isFollowing ? 'bg-accent text-accent-fg' : 'bg-surface-2 text-dim hover:bg-surface-3 hover:text-ink'
                )}
              >
                <Icon name={isFollowing ? 'bell' : 'bellOff'} size={13} />
                {isFollowing ? 'Following' : 'Follow'}
              </button>
              <span className="text-[12px] text-mute">
                {isFollowing ? 'You hear about new replies here.' : 'Replying follows it automatically.'}
              </span>
            </div>
          ) : null}

          {/* What is being answered, at the top and not repeated. */}
          {parent ? (
            <article className="rounded-[calc(14px*var(--radius-mul,1))] bg-surface-2 p-3">
              <header className="flex items-center gap-2">
                {parent.user ? <Avatar user={parent.user} size={26} /> : null}
                {parent.user ? <UserName user={parent.user} className="text-[13px] font-bold" /> : null}
                <span className="text-[11.5px] text-ghost">{ago(parent.createdAt)}</span>
              </header>
              <p className="m-0 mt-1.5 text-[13.5px] whitespace-pre-wrap text-ink">
                <EmojiText text={parent.body} size={16} />
              </p>
            </article>
          ) : null}

          <ul className="m-0 flex list-none flex-col gap-2 p-0">
            {(q.data?.replies ?? []).map((m) => (
              <li key={m.id} className="flex gap-2.5">
                {m.user ? <Avatar user={m.user} size={28} /> : null}
                <div className="min-w-0 flex-1">
                  <div className="flex items-baseline gap-2">
                    {m.user ? <UserName user={m.user} className="text-[13px] font-bold" /> : null}
                    <span className="text-[11px] text-ghost">{ago(m.createdAt)}</span>
                  </div>
                  <p className="m-0 text-[13.5px] whitespace-pre-wrap text-dim">
                    <EmojiText text={m.body} size={16} />
                  </p>
                </div>
              </li>
            ))}
          </ul>

          {!(q.data?.replies ?? []).length ? (
            <p className="m-0 py-4 text-center text-[13px] text-mute">Nobody has answered this yet.</p>
          ) : null}
        </div>
      )}
    </Modal>
  );
}
